import { action, observable, runInAction } from 'mobx';
import { SessionStore } from './session.store';
import { AggregateFunction, AgtAnalyticsStore, SelectColumn } from './agt.analytics.store';

export interface IAgtGoogleProfileModel {
	vtnrId: number;
	bm: number;
	bewertungen: number;
	bewertungsdurchschnitt: number;
	anzahlBilder: number;
	// anzahlFragen: number;
	ranking: number;
	neueBwertungen: number;
	selbstbau: number;
}

export class AgtGoogleProfileChangeModel {
	agtId: number;
	bm: number;
	bew: number;
	bewAvg: number;
	changeBew: number;
	changeNeueBew: number;
	constructor(agtId: number, bm: number, bew: number, bewAvg: number, changeBew: number, changeNeueBew: number) {
		this.agtId = agtId;
		this.bm = bm;
		this.bew = bew;
		this.bewAvg = bewAvg;
		this.changeBew = changeBew;
		this.changeNeueBew = changeNeueBew;

	}
}

export class AgtGoogleProfileModel implements IAgtGoogleProfileModel {
	vtnrId: number;
	bm: number;
	bewertungen: number;
	bewertungsdurchschnitt: number;
	anzahlBilder: number;
	// anzahlFragen: number;
	ranking: number;
	neueBwertungen: number;
	selbstbau: number;

	// Legacy compatibility properties
	agtId: number;
	j: any;

	constructor(data: any) {
		this.vtnrId = data.aid || 0;
		this.bm = data.bm ? parseInt(data.bm) : 0;
		this.bewertungen = data.b || 0;
		this.bewertungsdurchschnitt = data.a || 0;
		this.anzahlBilder = data.i || 0;
		// this.anzahlFragen = data.q || 0;
		this.ranking = data.r || 0;
		this.neueBwertungen = data.nb || 0;
		this.selbstbau = data.sb || 0;

		// Legacy compatibility - alias properties
		this.agtId = this.vtnrId;

		// Legacy j property for backwards compatibility
		this.j = {
			b: this.bewertungen,
			a: this.bewertungsdurchschnitt,
			i: this.anzahlBilder,
			// q: this.anzahlFragen,
			r: this.ranking,
			nb: this.neueBwertungen,
			sb: this.selbstbau,

		};
	}
}

export class AgtGoogleProfileStore extends AgtAnalyticsStore {
	constructor(session: SessionStore) {
		super(session, {
			tableName: 'agentur_googleprofile',
			defaultColumns: [
				{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, alias: 'aid' },
				{ column: 'bm', aggregateFunction: AggregateFunction.NONE },
				{ column: 'bewertungen', aggregateFunction: AggregateFunction.NONE, alias: 'b' },
				{ column: 'bewertungsdurchschnitt', aggregateFunction: AggregateFunction.NONE, alias: 'a' },
				{ column: 'anzahl_bilder', aggregateFunction: AggregateFunction.NONE, alias: 'i' },
				// { column: 'anzahl_fragen', aggregateFunction: AggregateFunction.NONE, alias: 'q' },
				{ column: 'ranking', aggregateFunction: AggregateFunction.NONE, alias: 'r' },
				{ column: 'neue_bewertungen', aggregateFunction: AggregateFunction.NONE, alias: 'nb' },
				{ column: 'selbstbau', aggregateFunction: AggregateFunction.NONE, alias: 'sb' },
			],
		});
		this.queryName = '?googleProfile';
	}

	async getDistinctBMs(): Promise<number[]> {
		const selectColumns: SelectColumn[] = [
			{ column: 'bm', aggregateFunction: AggregateFunction.NONE, distinct: true },
		];
		const results = await this.query(selectColumns);
		return results.map(data => parseInt(data.bm)).sort((a, b) => a - b);
	}

	@action
	async loadByBm(bm: number): Promise<AgtGoogleProfileModel[]> {
		let whereClause = 'bm = ' + bm;
		if (!bm) {
			debugger;
		}
		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.map(data => new AgtGoogleProfileModel(data));
	}

	@observable
	changedItems: AgtGoogleProfileChangeModel[] = [];

	@action
	async loadTimeSeriesByAgt(agtId: number): Promise<AgtGoogleProfileChangeModel[]> {
		const selectColumns: SelectColumn[] = [
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, groupBy: false, alias: 'aid' },
			{ column: 'bm', aggregateFunction: AggregateFunction.NONE, groupBy: false },
			{ column: 'bewertungen', aggregateFunction: AggregateFunction.NONE, alias: 'a' },
			{ column: 'neue_bewertungen', aggregateFunction: AggregateFunction.NONE, alias: 'nb' },
			{ column: 'bewertungsdurchschnitt', aggregateFunction: AggregateFunction.NONE, alias: 'b' },
			{ column: 'bewertungschange', aggregateFunction: AggregateFunction.NONE, alias: 'c' },
		];

		const results = await this.query(selectColumns, `a.vtnr_id = ${agtId}`);

		return results
			.map((data) => new AgtGoogleProfileChangeModel(
				data.aid,
				data.bm ? data.bm : 0,
				data.a ? data.a : 0,
				data.b ? data.b : 0,
				data.c ? data.c : 0,
				data.nb ? data.nb : 0,
			))
			.sort((a, b) => a.bm - b.bm);
	}

	@action
	async loadTimeSeriesByBm(minBm?: number, maxBm?: number): Promise<AgtGoogleProfileChangeModel[]> {
		console.time('AgtGoogleProfileStore.loadTimeSeriesByBm');
		let whereClause = '';
		if (minBm && maxBm) {
			whereClause = `bm BETWEEN ${minBm} AND ${maxBm}`;
		} else if (minBm) {
			whereClause = `bm >= ${minBm}`;
		} else if (maxBm) {
			whereClause = `bm <= ${maxBm}`;
		}


		const selectColumns: SelectColumn[] = [
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, groupBy: false, alias: 'aid' },
			{ column: 'bm', aggregateFunction: AggregateFunction.NONE, groupBy: false },
			{ column: 'bewertungen', aggregateFunction: AggregateFunction.NONE, alias: 'a' },
			{ column: 'neue_bewertungen', aggregateFunction: AggregateFunction.NONE, alias: 'nb' },
			{ column: 'bewertungsdurchschnitt', aggregateFunction: AggregateFunction.NONE, alias: 'b' },
			{ column: 'bewertungschange', aggregateFunction: AggregateFunction.NONE, alias: 'c' }, // hardcoded trick property. search for it on server
		];

		const results = await this.query(selectColumns, whereClause); //, 'vtnr_id, bm ASC');


		runInAction(() => {
			this.changedItems = results.map((data, i) => new AgtGoogleProfileChangeModel(
				data.aid,
				data.bm ? data.bm : 0,
				data.a ? data.a : 0,
				data.b ? data.b : 0,
				data.c ? data.c : 0,
				data.nb ? data.nb : 0,
			));
		});
		console.timeEnd('AgtGoogleProfileStore.loadTimeSeriesByBm');

		return this.changedItems
	}
}